// A sign-in started from a vault's wall QR is a "vault session": the backend
// caps it at VAULT_SESSION_LIFETIME (4h) from sign-in. Unlike the normal
// session (in memory only, so a reload means signing in again), it's kept in
// localStorage so scanning the next vault — a fresh page load from the phone
// camera — doesn't ask for sign-in again. It's only ever restored on a vault
// page, so it never signs anyone into the rest of the site.
const KEY = "pip.vaultSession";
// Set when a vault session runs out, so the login page can say why.
const EXPIRED_KEY = "pip.vaultSessionExpired";

export const VAULT_SESSION = "vault";

export const isVaultPath = (pathname) =>
  typeof pathname === "string" && pathname.startsWith("/vaults/");

// When the token stops working, in ms — read from its own `exp` claim so it
// always matches what the backend enforces.
export function tokenExpiresAt(token) {
  try {
    const payload = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const { exp } = JSON.parse(window.atob(payload));
    return typeof exp === "number" ? exp * 1000 : null;
  } catch (_error) {
    return null;
  }
}

export function saveVaultSession({ accessToken, refreshToken }) {
  const expiresAt = tokenExpiresAt(refreshToken);
  if (!expiresAt) return null;
  try {
    window.localStorage.setItem(KEY, JSON.stringify({ accessToken, refreshToken, expiresAt }));
  } catch (_error) {
    // Storage blocked — still works for this page, just not across scans.
  }
  return expiresAt;
}

// The stored session, or null. One that has already run out is cleared and
// flagged as expired on the way.
export function loadVaultSession() {
  let session = null;
  try {
    session = JSON.parse(window.localStorage.getItem(KEY));
  } catch (_error) {
    return null;
  }
  if (!session?.accessToken || !session?.refreshToken || !session?.expiresAt) return null;
  if (Date.now() >= session.expiresAt) {
    clearVaultSession();
    markVaultSessionExpired();
    return null;
  }
  return session;
}

export function updateVaultSessionAccessToken(accessToken) {
  try {
    const session = JSON.parse(window.localStorage.getItem(KEY));
    if (session) window.localStorage.setItem(KEY, JSON.stringify({ ...session, accessToken }));
  } catch (_error) {
    // Nothing stored to update.
  }
}

export function clearVaultSession() {
  try {
    window.localStorage.removeItem(KEY);
  } catch (_error) {
    // Nothing to clear.
  }
}

export function markVaultSessionExpired() {
  try {
    window.sessionStorage.setItem(EXPIRED_KEY, "1");
  } catch (_error) {
    // The login page just won't say why.
  }
}

export function takeVaultSessionExpired() {
  try {
    const expired = window.sessionStorage.getItem(EXPIRED_KEY) === "1";
    window.sessionStorage.removeItem(EXPIRED_KEY);
    return expired;
  } catch (_error) {
    return false;
  }
}
